import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { api } from "../utils/api";
import PageMeta from "../components/PageMeta";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

type Match = { id: string; name: string; avatar_url?: string; matched_phone: string; friendship_status: "none" | "pending_sent" | "pending_received" | "accepted" };

const normalizePhone = (raw: string) => raw.replace(/\D/g, "").slice(-9);

// Version "best effort" de OLTODO/app/invite-contacts.tsx : l'API Contact Picker du web
// (navigator.contacts.select) n'existe quasiment que sur Android Chrome — jamais Safari iOS ni
// desktop — donc ce point d'entrée reste masqué partout ailleurs (voir Invite.tsx). Pas d'envoi de
// SMS possible depuis un navigateur : on utilise le Web Share API déjà en place pour le lien de
// parrainage (voir Invite.tsx) pour laisser l'utilisateur choisir SMS/WhatsApp/etc.
export default function InviteContacts() {
  const navigate = useNavigate();
  const { user, lang } = useApp();
  const supported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const [picked, setPicked] = useState<{ name: string; tel: string[] }[] | null>(null);
  const [matching, setMatching] = useState(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [unmatched, setUnmatched] = useState<{ name: string; tel: string[] }[]>([]);
  const [sendingRequestId, setSendingRequestId] = useState<string | null>(null);
  const [shared, setShared] = useState(false);

  const pickContacts = async () => {
    try {
      const props = ["name", "tel"];
      const selected: { name: string[]; tel: string[] }[] = await (navigator as any).contacts.select(props, { multiple: true });
      const list = selected.map(c => ({ name: c.name?.[0] || "?", tel: (c.tel || []).filter(Boolean) })).filter(c => c.tel.length > 0);
      setPicked(list);
      setMatching(true);
      const allPhones = list.flatMap(c => c.tel);
      const data = await api.post("/users/me/contacts/match", { phones: allPhones });
      const found: Match[] = data?.matches || [];
      const matchedNorm = new Set(found.map(m => m.matched_phone));
      const notMatched = list.filter(c => !c.tel.some(p => matchedNorm.has(normalizePhone(p))));
      setMatches(found);
      setUnmatched(notMatched);
    } catch {
      // annulé par l'utilisateur ou erreur navigateur — rien à afficher
    }
    setMatching(false);
  };

  const sendFriendRequest = async (m: Match) => {
    if (sendingRequestId) return;
    setSendingRequestId(m.id);
    try {
      await api.post(`/users/${m.id}/friend-request`, {});
      setMatches(prev => prev.map(x => x.id === m.id ? { ...x, friendship_status: "pending_sent" } : x));
    } catch {
      alert(t(lang, "error_connection"));
    }
    setSendingRequestId(null);
  };

  const shareInvite = async () => {
    const url = `${window.location.origin}/join?ref=${user?.referral_code}`;
    const text = "Rejoins-moi sur OLTODO, le réseau social qui reconnecte à sa ville !";
    if (navigator.share) {
      try { await navigator.share({ title: "OLTODO", text, url }); return; } catch { return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {}
  };

  if (!supported) {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px", textAlign: "center" }}>
        <PageMeta title={t(lang, "invite_contacts_title")} />
        <div style={{ marginTop: 60, color: "#999", fontSize: 13.5 }}>{t(lang, "invite_contacts_empty")}</div>
        <button onClick={() => navigate("/invite")} style={{ marginTop: 16, border: "none", background: "none", color: "#0a0a0a", fontWeight: 700, fontSize: 13.5, cursor: "pointer", textDecoration: "underline" }}>←</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px" }}>
      <PageMeta title={t(lang, "invite_contacts_title")} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{t(lang, "invite_contacts_title")}</span>
      </div>

      {picked === null ? (
        <>
          <p style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, marginBottom: 20 }}>{t(lang, "invite_contacts_permission_body")}</p>
          <button onClick={pickContacts} style={{ width: "100%", padding: 15, borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            {t(lang, "invite_contacts_allow_access")}
          </button>
        </>
      ) : matching ? (
        <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>{t(lang, "msg_loading")}</div>
      ) : (
        <>
          {matches.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#666", marginBottom: 10 }}>{t(lang, "invite_contacts_already_here")} ({matches.length})</div>
              {matches.map(m => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ color: "white", fontWeight: 700, fontSize: 13 }}>{initials(m.name)}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</div>
                  {m.friendship_status === "accepted" ? (
                    <span style={{ fontSize: 12.5, color: "#999", fontWeight: 700 }}>{t(lang, "invite_contacts_already_friend")}</span>
                  ) : m.friendship_status === "pending_sent" ? (
                    <span style={{ fontSize: 12.5, color: "#999", fontWeight: 700 }}>{t(lang, "invite_contacts_request_sent")}</span>
                  ) : m.friendship_status === "pending_received" ? (
                    <button onClick={() => navigate("/notifications?tab=demandes")} style={{ border: "none", background: "none", color: "#0a0a0a", fontWeight: 700, fontSize: 12.5, cursor: "pointer", textDecoration: "underline" }}>{t(lang, "invite_contacts_respond")}</button>
                  ) : (
                    <button onClick={() => sendFriendRequest(m)} disabled={sendingRequestId === m.id} style={{ border: "none", borderRadius: 10, padding: "7px 14px", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                      {t(lang, "bday_gift_add_btn")}
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {unmatched.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#666", marginTop: matches.length > 0 ? 24 : 0, marginBottom: 10 }}>{t(lang, "invite_contacts_to_invite")} ({unmatched.length})</div>
              {unmatched.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#666", fontWeight: 700, fontSize: 13 }}>{initials(c.name)}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                </div>
              ))}
              <button onClick={shareInvite} style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 16 }}>
                {shared ? t(lang, "invite_contacts_sms_sent") : t(lang, "invite_contacts_send_sms")}
              </button>
            </>
          )}

          {matches.length === 0 && unmatched.length === 0 && (
            <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>{t(lang, "invite_contacts_empty")}</div>
          )}
        </>
      )}
    </div>
  );
}
